import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useEffect, useRef, useState } from "react";
import wasteData from "../data/mockData.ts";
import FilterPanel from "./FilterPanel.tsx";
import SearchBar from "./SearchBar.tsx";
import Supercluster from "supercluster";
import "../styles/MapScreen.css";

export default function MapScreen() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null as maplibregl.Map | null);
  const [showPaper, setShowPaper] = useState(true);
  const [showGlass, setShowGlass] = useState(true);
  const clusterIndex = useRef(
    new Supercluster({
      radius: 40,
      maxZoom: 16,
    })
  );

  useEffect(() => {
    if (!mapContainer.current) return;
    mapRef.current = new maplibregl.Map({
      container: mapContainer.current,
      style:
        "https://api.maptiler.com/maps/streets/style.json?key=5N49Er4fDCjwggmOCPDp",
      center: [4.8942, 52.4007],
      zoom: 15,
    });

    mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right");

    return () => mapRef.current?.remove();
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    // Filter and prepare data for clustering
    const filteredData = wasteData.Assets.filter(
      (asset) =>
        (asset.AssetType === "PaperGarbage" && showPaper) ||
        (asset.AssetType === "GlassGarbage" && showGlass)
    ).map((asset) => ({
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: [asset.Longitude, asset.Latitude],
      },
      properties: {
        AssetType: asset.AssetType,
      },
    }));

    clusterIndex.current.load(filteredData);

    const updateMarkers = () => {
      if (!mapRef.current) return;

      // Remove old markers
      document.querySelectorAll(".map-marker").forEach((m) => m.remove());

      const bounds = mapRef.current.getBounds().toArray().flat() as [
        number,
        number,
        number,
        number
      ];
      const zoom = mapRef.current.getZoom();
      const clusters = clusterIndex.current.getClusters(bounds, Math.floor(zoom));

      clusters.forEach((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const el = document.createElement("div");
        el.className = "map-marker";

        if (cluster.properties.cluster) {
          el.style.width = "40px";
          el.style.height = "40px";
          el.style.background = "rgba(0, 123, 255, 0.6)";
          el.style.borderRadius = "50%";
          el.style.display = "flex";
          el.style.alignItems = "center";
          el.style.justifyContent = "center";
          el.style.color = "white";
          el.style.fontWeight = "bold";
          el.textContent = cluster.properties.point_count;
        } else {
          el.style.width = "32px";
          el.style.height = "32px";
          el.style.border = `2px solid ${
            cluster.properties.AssetType === "PaperGarbage" ? "red" : "blue"
          }`;
          el.style.borderRadius = "50%";
          el.style.background = "#fff";
          el.style.backgroundImage = `url(${
            cluster.properties.AssetType === "PaperGarbage"
              ? "assets/paper.png"
              : "assets/glass.png"
          })`;
          el.style.backgroundSize = "cover";
        }

        new maplibregl.Marker({ element: el })
          .setLngLat([longitude, latitude])
          .addTo(mapRef.current!);
      });
    };

    updateMarkers();
    mapRef.current.on("moveend", updateMarkers);

    return () => {
      mapRef.current?.off("moveend", updateMarkers);
    };
  }, [showPaper, showGlass]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Floating SearchBar at top center */}
      <div className="floating-searchbar-container">
        <div className="floating-searchbar-inner">
          <SearchBar />
        </div>
      </div>
      <FilterPanel
        showPaper={showPaper}
        setShowPaper={setShowPaper}
        showGlass={showGlass}
        setShowGlass={setShowGlass}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
}
